/**
 * useOverlayRegistration (T27.2.7.3) — register an overlay's close handler in the
 * shared overlayRegistry while it is open, so the app-level hardware-Back handler
 * (useAndroidBackButton) can close it on a Back press.
 *
 * Usage in an overlay component:
 *   useOverlayRegistration(open, onClose);
 *
 * While `open` is true the overlay's `close` callback sits on the LIFO stack;
 * when `open` flips false (or the component unmounts) the entry is removed. The
 * `close` callback is captured in a ref so a re-render with a new function
 * identity does not churn the registration — the same entry stays on the stack
 * and always invokes the latest handler.
 *
 * This adds NOTHING to web/desktop behavior: the registry is only consumed by the
 * native-gated Back handler. Escape / backdrop close paths run unchanged.
 */

import { useEffect, useRef } from 'react';
import { registerOverlay, type OverlayCloseHandler } from './overlayRegistry';

export function useOverlayRegistration(open: boolean, close: OverlayCloseHandler): void {
  // Hold the latest close handler so re-renders that change its identity don't
  // re-register; the stack entry calls through this ref.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return undefined;
    const deregister = registerOverlay(() => closeRef.current());
    return deregister;
  }, [open]);
}
